/**
 * SegmentPage - Template para páginas por segmento
 */
import { Link } from 'react-router-dom'
import { ArrowRight, Play, CheckCircle2 } from 'lucide-react'
import Navbar from './Navbar'
import Footer from './Footer'

interface SegmentPageProps {
  segment: {
    name: string
    tagline: string
    description: string
    icon: string
    color: string
    gradient: string
    painPoints: string[]
    benefits: string[]
    nrs: string[]
    stats: { value: string; label: string }[]
    testimonial?: {
      quote: string
      author: string
      role: string
      company: string
    }
  }
}

export default function SegmentPage({ segment }: SegmentPageProps) {
  return (
    <div className="min-h-screen bg-[#0B1120]">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-30" />
        <div 
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] rounded-full blur-3xl"
          style={{ background: `linear-gradient(to bottom right, ${segment.color}33, transparent)` }}
        />
        
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6"
              style={{ 
                background: `${segment.color}1a`,
                borderColor: `${segment.color}33`,
                color: segment.color,
                border: '1px solid'
              }}
            >
              <span className="text-xl">{segment.icon}</span>
              WORKSTREAM PARA {segment.name.toUpperCase()}
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              {segment.tagline.split(' ').map((word, i, arr) => 
                i === arr.length - 1 ? (
                  <span key={i} style={{ background: segment.gradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    {word}
                  </span>
                ) : word + ' '
              )}
            </h1>
            
            <p className="text-xl text-slate-400 mb-8 max-w-2xl mx-auto">
              {segment.description}
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/demo"
                className="px-8 py-4 rounded-xl font-semibold text-white transition-all flex items-center gap-2"
                style={{ 
                  background: segment.gradient,
                  boxShadow: `0 4px 14px 0 ${segment.color}63`
                }}
              >
                Testar Grátis
                <ArrowRight className="w-5 h-5" />
              </Link>
              <button className="px-8 py-4 rounded-xl font-semibold text-white bg-slate-800 border border-slate-700 hover:bg-slate-700 transition-all flex items-center gap-2">
                <Play className="w-5 h-5" />
                Ver Demo
              </button>
            </div>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16 max-w-3xl mx-auto">
            {segment.stats.map((stat) => (
              <div key={stat.label} className="text-center p-4 rounded-xl bg-slate-800/50 border border-slate-700">
                <p className="text-3xl font-bold" style={{ color: segment.color }}>{stat.value}</p>
                <p className="text-sm text-slate-400">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Pain Points */}
      <section className="py-20 bg-[#0F172A]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Desafios do Setor de {segment.name}
              </h2>
              <p className="text-slate-400">
                Problemas que o WorkStream resolve para você
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {segment.painPoints.map((point, i) => (
                <div 
                  key={i}
                  className="p-4 rounded-xl bg-slate-800/50 border border-slate-700 flex items-start gap-3"
                >
                  <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: segment.color }} />
                  <span className="text-slate-300">{point}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      {/* Benefits */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Benefícios para {segment.name}
              </h2>
              <p className="text-slate-400">
                Como o WorkStream transforma sua operação
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {segment.benefits.map((benefit, i) => (
                <div 
                  key={i}
                  className="p-6 rounded-2xl bg-slate-800/50 border border-slate-700 text-center hover:border-opacity-50 transition-all"
                  style={{ '--hover-color': segment.color } as any}
                >
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4 text-2xl"
                    style={{ background: `${segment.color}1a` }}
                  >
                    {['🎯', '📊', '🛡️', '⚡', '💰', '🤝'][i % 6]}
                  </div>
                  <p className="text-slate-300">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      {/* NRs */}
      {segment.nrs.length > 0 && (
        <section className="py-16 bg-[#0F172A]">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h3 className="text-xl font-semibold text-white mb-6">
                NRs Relevantes para {segment.name}
              </h3>
              <div className="flex flex-wrap justify-center gap-3">
                {segment.nrs.map((nr) => (
                  <span 
                    key={nr}
                    className="px-4 py-2 rounded-full text-sm font-medium"
                    style={{ 
                      background: `${segment.color}1a`,
                      color: segment.color,
                      border: `1px solid ${segment.color}33`
                    }}
                  >
                    {nr}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
      
      {/* Testimonial */}
      {segment.testimonial && (
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div 
                className="p-8 rounded-2xl border"
                style={{ 
                  background: `${segment.color}0d`,
                  borderColor: `${segment.color}33`
                }}
              >
                <p className="text-xl text-slate-300 italic mb-6">
                  "{segment.testimonial.quote}"
                </p>
                <div className="flex items-center gap-4">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold"
                    style={{ background: `${segment.color}33`, color: segment.color }}
                  >
                    {segment.testimonial.author.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-white">{segment.testimonial.author}</p>
                    <p className="text-sm text-slate-400">
                      {segment.testimonial.role}, {segment.testimonial.company}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
      
      {/* CTA */}
      <section 
        className="py-20"
        style={{ background: `linear-gradient(to bottom right, ${segment.color}1a, transparent)` }}
      >
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Pronto para transformar sua {segment.name.toLowerCase()}?
          </h2>
          <p className="text-slate-400 mb-8 max-w-xl mx-auto">
            Comece gratuitamente por 14 dias. Sem cartão de crédito.
          </p>
          <Link
            to="/demo"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-white transition-all"
            style={{ 
              background: segment.gradient,
              boxShadow: `0 4px 14px 0 ${segment.color}63`
            }}
          >
            Começar Teste Grátis
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
      
      <Footer />
    </div>
  )
}

